import React, { ReactNode, useCallback, useState } from "react"
import Image from "next/image"

import { useUpdateableState } from "@lib"

import Accordion from "./Accordion"

export type FilterOption = { title: string, value: string }

const AccordionFilterOptions: React.FunctionComponent<{
    title?: ReactNode,
    options?: FilterOption[]
    values?: string[],
    onChange?: (selectedValues: string[]) => void
}> = ({
    title,
    options,
    values,
    onChange
}) => {
    const [isExpanded, setIsExpanded] = useState<boolean>()
    const [selectedValues, setSelectedValues] = useUpdateableState(
        values || [],
        (current, next) => {
            return (current || []).sort().join("|") !== (next || []).sort().join("|")
        }
    )

    const optionClickHandler = useCallback((option: FilterOption) => {
        const isSelected = selectedValues?.includes(option.value) === true
        let nextSelectedValues: string[]
        if (isSelected) {
            // Remove from selectedValues
            nextSelectedValues = (selectedValues || []).filter((each) => each !== option.value)
        } else {
            // Add to selectedValues
            nextSelectedValues = (selectedValues || []).concat(option.value)
        }

        setSelectedValues(nextSelectedValues)
        onChange && onChange(nextSelectedValues)
    }, [selectedValues, setSelectedValues, onChange])

    const removeFilterClickHandler = useCallback((option: FilterOption) => {
        const nextSelectedValues = (selectedValues || []).filter((each) => each !== option.value)
        setSelectedValues(nextSelectedValues)
        onChange && onChange(nextSelectedValues)
    }, [selectedValues, setSelectedValues, onChange])

    return (
        <Accordion
            title={<h4 className="text-body text-black-900">{title}</h4>}
            summary={selectedValues && selectedValues.length > 0 && (
                <div className="flex flex-wrap gap-[16px]">
                    {selectedValues?.map((each) => {
                        const option = options?.find((option) => option.value === each)
                        if (!option) return null
                        return (
                            <div key={each} className="flex chip-primary-selected">
                                <span className="flex-auto">{option?.title || each}</span>
                                <button type="button" className="w-[24px] h-[24px] ml-[8px] my-auto"
                                    onClick={() => removeFilterClickHandler(option)}>
                                    <Image width={24} height={24} objectFit="contain"
                                        src="/images/ic_close_small_white.svg"
                                        alt="Remove Filter" />
                                </button>
                            </div>
                        )
                    })}
                </div>
            )}
            onExpand={() => setIsExpanded(true)}
            onCollapse={() => setIsExpanded(false)}>

            <div className="flex flex-wrap gap-[16px] px-[20px] pt-0 pb-[32px]">
                {(options || []).map((each) => {
                    const isSelected = selectedValues?.includes(each.value) === true
                    return (
                        <div key={each.value} className={isSelected ? "chip-primary-selected" : "chip-primary"}
                            onClick={() => optionClickHandler(each)}>
                            <span>{each.title}</span>
                        </div>
                    )
                })}
            </div>
        </Accordion>
    )
}

export default AccordionFilterOptions
